import { useCallback, useEffect, useMemo, useState } from 'react';
import { replace } from 'lodash';
import {
  CATEGORY,
  Documentation,
  DocumentationCategoryRow,
  FormAddDocument
} from 'types/documents';
import {
  createDocumentation,
  deleteDocumentationAsset,
  deleteDocumentationLibrary,
  deleteDocumentationModel,
  getDocumentationCategories,
  getDocumentations,
  getDocumentationsAssets,
  getDocumentationsModel
} from 'services/documentation';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { usePaging } from 'hooks/usePaging';
import { useSearchParams } from 'react-router-dom';
import { useFlash } from 'contexts/flash';
import { useClientOptions } from 'hooks/useGetClients';

/** Default slug when categories API unavailable */
const DEFAULT_LIBRARY_SLUG = CATEGORY.SLD;

export const useInjection = () => {
  const { setFlash } = useFlash();
  const [searchParams, setSearchParams] = useSearchParams();
  const [valueSearch, setValueSearch] = useState<string>('');
  const keywordFromUrl = searchParams.get('keyword') ?? '';
  useEffect(() => {
    setValueSearch(keywordFromUrl);
  }, [keywordFromUrl]);
  const [documents, setDocuments] = useState<Documentation[]>([]);
  const [libraryCategories, setLibraryCategories] = useState<DocumentationCategoryRow[]>([]);
  const [showModalAddDocument, setShowModalAddDocument] = useState<boolean>(false);
  const { pagingInfo, setCurrentPage, setTotal } = usePaging({
    rowsPerPage: 8
  });

  const { clientOptions: optionClient } = useClientOptions();

  const reloadLibraryCategories = useCallback(async () => {
    try {
      const res = await getDocumentationCategories();
      setLibraryCategories(res.categories ?? []);
    } catch {
      setFlash({ type: 'error', message: 'Could not load documentation categories' });
    }
  }, [setFlash]);

  useEffect(() => {
    void reloadLibraryCategories();
  }, [reloadLibraryCategories]);

  const categoryFromUrl = searchParams.get('category');

  /** Keep URL ?category valid for library tabs (Assets / Models unchanged). */
  useEffect(() => {
    if (libraryCategories.length === 0) return;

    const assets = CATEGORY.ASSETS;
    const models = CATEGORY.MODELS;

    const isSpecial = categoryFromUrl === assets || categoryFromUrl === models;
    const hasLibrarySlug =
      categoryFromUrl && libraryCategories.some((c) => c.slug === categoryFromUrl);

    if (categoryFromUrl && !isSpecial && !hasLibrarySlug) {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          p.set('category', libraryCategories[0]?.slug ?? DEFAULT_LIBRARY_SLUG);
          return p;
        },
        { replace: true }
      );
      return;
    }

    if (!categoryFromUrl && !isSpecial) {
      setSearchParams(
        (prev) => {
          const p = new URLSearchParams(prev);
          p.set('category', libraryCategories[0]?.slug ?? DEFAULT_LIBRARY_SLUG);
          return p;
        },
        { replace: true }
      );
    }
  }, [libraryCategories, categoryFromUrl, setSearchParams]);

  const resolvedCategorySlug = useMemo(() => {
    return searchParams.get('category') ?? libraryCategories[0]?.slug ?? DEFAULT_LIBRARY_SLUG;
  }, [libraryCategories, searchParams]);

  const handleChangePage = useCallback(
    (value: number) => {
      setCurrentPage(value);
      searchParams.set(`page`, value.toString());
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams, setCurrentPage]
  );

  const fetchData = useCallback(async () => {
    const categoryCurrent =
      searchParams.get('category') ?? libraryCategories[0]?.slug ?? DEFAULT_LIBRARY_SLUG;

    const params = {
      size: 8,
      page: Number(searchParams.get('page')) || 1,
      keyword: searchParams.get('keyword') || undefined,
      clientId: searchParams.get('clientId') || undefined
    };

    let data;
    if (categoryCurrent === CATEGORY.ASSETS) {
      data = await getDocumentationsAssets(params);
    } else if (categoryCurrent === CATEGORY.MODELS) {
      data = await getDocumentationsModel(params);
    } else {
      data = await getDocumentations({ ...params, category: categoryCurrent });
    }
    setDocuments(data.documentations as Documentation[]);
    setCurrentPage(Number(searchParams.get('page')) || 1);
    setTotal(data.total);
  }, [libraryCategories, searchParams, setCurrentPage, setTotal]);

  const handleSearch = useCallback(() => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      const kw = valueSearch.trim();
      if (kw) {
        p.set('keyword', kw);
      } else {
        p.delete('keyword');
      }
      p.set('page', '1');
      return p;
    });
  }, [setSearchParams, valueSearch]);

  const filterByTypeClient = useCallback(
    (clientId: string) => {
      searchParams.set(`clientId`, clientId);
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams]
  );

  const handleChangeCategory = useCallback(
    (category: string) => {
      setSearchParams({ category });
      setValueSearch('');
    },
    [setSearchParams]
  );

  const { asyncCallback: asyncGetDocument, isLoading } = useAsyncCallback(fetchData, []);

  useEffect(() => {
    void asyncGetDocument();
  }, [searchParams, libraryCategories.length, asyncGetDocument]);

  const handleAddDocument = useCallback(
    async (value: FormAddDocument) => {
      setShowModalAddDocument(false);
      const formData = new FormData();
      const blob = new Blob([value.file], { type: value.file?.type });
      formData.append('file', blob, value.file?.name);
      const cat = typeof value.category === 'string' ? value.category : resolvedCategorySlug;
      formData.append('category', cat);
      formData.append('name', value.name);
      if (value?.clientIds && value.clientIds.length > 0) {
        let selectedClientIds = value.clientIds;
        if (selectedClientIds.includes('all')) {
          selectedClientIds = optionClient.map((opt) => opt.value as string);
        }
        const clientLabelById = new Map(
          optionClient.map((opt) => [String(opt.value), String(opt.label)])
        );
        [...selectedClientIds]
          .filter((id) => id !== 'all')
          .sort((a, b) =>
            (clientLabelById.get(a) ?? '').localeCompare(clientLabelById.get(b) ?? '', undefined, {
              sensitivity: 'base'
            })
          )
          .forEach((id) => {
            formData.append('clientIds[]', id);
          });
      }
      await createDocumentation(formData);
      setFlash({ type: 'success', message: 'Create documentation successfully' });
      await fetchData();
    },
    [fetchData, resolvedCategorySlug, optionClient, setFlash]
  );

  const { asyncCallback: asyncAddDocument } = useAsyncCallback(handleAddDocument, []);

  const handleDeleteDocumentation = useCallback(
    async (doc: Documentation) => {
      if (!window.confirm('Delete this documentation file?')) return;
      try {
        const categoryCurrent =
          searchParams.get('category') ?? libraryCategories[0]?.slug ?? DEFAULT_LIBRARY_SLUG;
        const id = Number(doc.id);
        if (Number.isNaN(id)) {
          setFlash({ type: 'error', message: 'Invalid documentation id' });
          return;
        }
        if (categoryCurrent === CATEGORY.ASSETS) {
          await deleteDocumentationAsset(id);
        } else if (categoryCurrent === CATEGORY.MODELS) {
          await deleteDocumentationModel(id);
        } else {
          await deleteDocumentationLibrary(id);
        }
        setFlash({ type: 'success', message: 'Documentation deleted' });
        await fetchData();
      } catch {
        setFlash({ type: 'error', message: 'Could not delete documentation' });
      }
    },
    [fetchData, libraryCategories, searchParams, setFlash]
  );

  const optionCategoryDynamic = useMemo(
    () =>
      libraryCategories.map((c) => ({
        value: c.slug,
        label: replace(c.slug, '_', ' ')
      })),
    [libraryCategories]
  );

  const categoryActive = searchParams.get('category') ?? resolvedCategorySlug;

  return {
    valueSearch,
    setValueSearch,
    valueFilterClient: searchParams.get('clientId'),
    handleSearch,
    filterByTypeClient,
    pagingInfo,
    handleChangePage,
    documents,
    handleChangeCategory,
    categoryActive,
    loading: isLoading,
    optionClient,
    libraryCategories,
    reloadLibraryCategories,
    optionCategory: optionCategoryDynamic,
    showModalAddDocument,
    setShowModalAddDocument,
    asyncAddDocument,
    handleDeleteDocumentation
  };
};
